import React from 'react';
import I18n from 'i18n-js';
import { useGooglePayPaymentRequest } from './useGooglePayPaymentRequest';
import {
  core_apimessages_CheckoutFinalizeDigitalWalletPaymentRequest_ResultConfirmation as ResultConfirmation,
  core_apimessages_CheckoutGooglePayDetails as GooglePayDetails,
  core_apimessages_Money as Money,
} from '@reverbdotcom/commons/src/gql/graphql';
import { useGooglePayCallbacks } from './useGooglePayCallbacks';
import { Checkout } from './googlePayHelpers';
import { CheckoutStartHandler } from './googlePayManager';
import { CheckoutGooglePayButton } from './CheckoutGooglePayButton';
import { useUser } from '@reverbdotcom/commons/src/user_hooks';
import { useGooglePayAvailability } from './googlePayAvailability';
import { useViewTracking } from '@reverbdotcom/commons/src/use_tracking';
import { MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_types';

type EstimatedTotal = Pick<Money, 'amount' | 'currency'>;

interface Props {
  onPaymentProcessing: (checkoutId: string) => void;
  estimatedTotal: EstimatedTotal;
  googlePayDetails: GooglePayDetails;
  startCheckout: CheckoutStartHandler;
  onClick?: () => any;
  small?: boolean;
}

export function BuyNowGooglePayButton({
  onPaymentProcessing,
  googlePayDetails,
  estimatedTotal,
  startCheckout,
  onClick = undefined,
  small = false,
}: Props) {
  const {
    showButton,
    isAvailable,
    loading,
  } = useViewModel(googlePayDetails);

  const paymentRequest = useBuyNowPaymentRequest(estimatedTotal, googlePayDetails);

  const callbacks = useGooglePayCallbacks({
    startCheckout,
    countryCode: paymentRequest.transactionInfo.countryCode,
    resultConfirmation: ResultConfirmation.ASYNC,
    onSuccess: React.useCallback((checkout: Checkout) => {
      onPaymentProcessing(checkout.id);
    }, []),
  });

  useViewTracking({
    eventName: MParticleEventName.GooglePayOffered,
    componentName: 'BuyNowGooglePayButton',
    googlePayAvailableForDevice: isAvailable,
  }, !loading);


  if (!showButton) {
    return null;
  }

  return (
    <CheckoutGooglePayButton
      paymentRequest={paymentRequest}
      onPaymentDataChanged={callbacks.onPaymentDataChanged}
      onPaymentAuthorized={callbacks.onPaymentAuthorized}
      onLoadPaymentData={callbacks.onLoadPaymentData}
      onClick={onClick}
      small={small}
    />
  );
}

function useViewModel(googlePayDetails: GooglePayDetails) {
  const { isAvailable, loading } = useGooglePayAvailability({ googlePayDetails });

  return {
    isAvailable,
    loading,
    showButton: isAvailable,
  };
}

export function useBuyNowPaymentRequest(estimatedTotal: EstimatedTotal, googlePayDetails: GooglePayDetails) {
  const user = useUser();

  return useGooglePayPaymentRequest({
    googlePayDetails: googlePayDetails,
    emailRequired: user.loggedOut,
    shippingAddressRequired: true,
    phoneNumberRequired: true,
    callbackIntents: [
      'SHIPPING_ADDRESS',
      'PAYMENT_AUTHORIZATION',
    ],
    transactionInfo: {
      totalPriceStatus: 'ESTIMATED',
      totalPrice: estimatedTotal.amount,
      currencyCode: estimatedTotal.currency,
      totalPriceLabel: I18n.t('discovery.checkout.googlePay.total'),
      displayItems: [
        {
          type: 'LINE_ITEM',
          price: estimatedTotal.amount,
          label: I18n.t('discovery.checkout.googlePay.estimatedTotal'),
          status: 'FINAL',
        },
      ],
    },
  });
}
